import { useState, useEffect, useMemo, useCallback } from "react";
import { parseCharacters } from "@/utils/format";
import { getFieldConfiguration } from "../config/fieldConfigurations";
import type {
  EntityFieldType,
  FieldTypeConfiguration,
} from "../config/fieldConfigurations";
import type { EntityField } from "@/pages/digital-files/types";

interface UseFieldFormProps {
  type: EntityFieldType | null;
  initialField?: EntityField;
  isOpen: boolean;
}

interface UseFieldFormReturn {
  // Valores del formulario
  fieldName: string;
  keyValue: string;
  formValues: Record<string, unknown>;
  localListItems: string[];
  newListItem: string;
  newFileType: string;
  locked: boolean;

  // Estado de validación
  errors: Record<string, string>;
  isValid: boolean;
  isNew: boolean;

  // Funciones para actualizar valores
  setFieldName: (value: string) => void;
  setKeyValue: (value: string) => void;
  updateFormValue: (key: string, value: unknown) => void;
  setLocalListItems: (items: string[]) => void;
  setNewListItem: (value: string) => void;
  setNewFileType: (value: string) => void;
  setLocked: (value: boolean) => void;
  setDataCenterValue: (
    fieldKey: string,
    tableName: string,
    columnName: string,
  ) => void;
  // Funciones de utilidad
  reset: () => void;
  getFieldData: () => EntityField | null;
  validate: () => boolean;

  // Configuración del campo
  fieldConfig: FieldTypeConfiguration | null;
}

export const useFieldForm = ({
  type,
  initialField,
  isOpen,
}: UseFieldFormProps): UseFieldFormReturn => {
  // Estados del formulario
  const [fieldId, setFieldId] = useState("");
  const [fieldName, setFieldNameState] = useState("");
  const [keyValue, setKeyValue] = useState("");
  const [formValues, setFormValues] = useState<Record<string, unknown>>({});
  const [localListItems, setLocalListItems] = useState<string[]>([]);
  const [newListItem, setNewListItem] = useState("");
  const [newFileType, setNewFileType] = useState("");
  const [locked, setLocked] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Wrapper para setFieldName que valida en tiempo real
  const setFieldName = (value: string) => {
    setFieldNameState(value);

    // Validar en tiempo real si el nombre es "ID"
    if (value.trim().toUpperCase() === "ID") {
      setErrors((prev) => ({
        ...prev,
        fieldName: "No se puede crear un campo con el nombre 'ID'",
      }));
    } else {
      // Limpiar el error si existe
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.fieldName;
        return newErrors;
      });
    }
  };

  // Obtener la configuración del campo
  const fieldConfig = useMemo(() => {
    if (!type) return null;
    return getFieldConfiguration(type);
  }, [type]);

  const generateId = useCallback((): string => {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
      return `field_${crypto.randomUUID()}`;
    }
    return `field_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  }, []);

  // Inicializar valores cuando se abre el modal o cambia la configuración
  useEffect(() => {
    if (isOpen && fieldConfig) {
      if (initialField) {
        // Modo edición: cargar valores desde initialField
        setFieldId(initialField.id || generateId());
        setFieldName(initialField.name || "");
        setKeyValue(
          initialField.key || initialField.name
            ? parseCharacters(initialField.name.toLowerCase())
            : "",
        );

        const dynamicValues: Record<string, unknown> = {};
        fieldConfig.sections.forEach((section) => {
          section.fields.forEach((field) => {
            if (field.key !== "fieldName") {
              const value = initialField[field.key];
              dynamicValues[field.key] =
                value !== undefined ? value : field.defaultValue;
            }
          });
        });

        // Para campos de archivo: si allowedFileTypes está vacío, establecer allowAnyFileType = true
        if (initialField.allowedFileTypes !== undefined) {
          const fileTypes = Array.isArray(initialField.allowedFileTypes)
            ? initialField.allowedFileTypes
            : [];
          if (fileTypes.length === 0) {
            dynamicValues.allowAnyFileType = true;
          } else {
            dynamicValues.allowAnyFileType = false;
          }
        }

        fieldConfig.sections.forEach((section) => {
          section.fields.forEach((field) => {
            if (field.type === "datacenter") {
              const tableKey = `${field.key}_table`;
              const columnKey = `${field.key}_column`;

              // Para valueDatacenter (dropdown button), buscar options_table y options_column
              if (field.key === "valueDatacenter") {
                if (initialField.options_table && initialField.options_column) {
                  dynamicValues[tableKey] = initialField.options_table;
                  dynamicValues[columnKey] = initialField.options_column;
                }
              }
              // Para options_root_datacenter (campo select/local_list), buscar options_root_table y options_root_column
              else if (field.key === "options_root_datacenter") {
                if (
                  initialField.options_root_table &&
                  initialField.options_root_column
                ) {
                  dynamicValues[tableKey] = initialField.options_root_table;
                  dynamicValues[columnKey] = initialField.options_root_column;
                }
              }
              // Para otros campos datacenter genéricos
              else {
                if (initialField[tableKey] && initialField[columnKey]) {
                  dynamicValues[tableKey] = initialField[tableKey];
                  dynamicValues[columnKey] = initialField[columnKey];
                }
              }
            }
          });
        });
        const hasOptionsRootConfig =
          initialField.options_root_table ||
          initialField.options_root_source === "manual";

        if (
          hasOptionsRootConfig &&
          initialField.options &&
          Array.isArray(initialField.options)
        ) {
          fieldConfig.sections.forEach((section) => {
            section.fields.forEach((field) => {
              if (
                field.type === "local_list" &&
                field.key === "options_root_list"
              ) {
                dynamicValues[field.key] = initialField.options;
              }
            });
          });
        }

        // Lista del dropdown button desde configuration.options_dropdown
        if (
          initialField.options_dropdown &&
          Array.isArray(initialField.options_dropdown)
        ) {
          setLocalListItems(initialField.options_dropdown);
          fieldConfig.sections.forEach((section) => {
            section.fields.forEach((field) => {
              if (field.type === "local_list" && field.key === "localList") {
                dynamicValues[field.key] = initialField.options_dropdown;
              }
            });
          });
        }

        // Para campos de identificación: convertir documentType="auto" a allowUserChooseDocType=true
        if (type === "identification") {
          if (initialField.documentType === "auto") {
            dynamicValues.allowUserChooseDocType = true;
            dynamicValues.documentType = ""; // Limpiar para que no se muestre en el dropdown
          }
        }

        if (initialField.options_source) {
          dynamicValues.valueSource = initialField.options_source;
        }
        if (initialField.options_root_source) {
          dynamicValues.options_root_source = initialField.options_root_source;
        } else if (initialField.options_root_table) {
          dynamicValues.options_root_source = "database";
        } else if (
          initialField.options &&
          Array.isArray(initialField.options) &&
          initialField.options.length > 0
        ) {
          if (!initialField.options_table) {
            dynamicValues.options_root_source = "manual";
          }
        }

        if (initialField.buttonEnabled !== undefined) {
          dynamicValues.hasDropdownButton = initialField.buttonEnabled;
        }

        // Actualizar formValues una sola vez con todos los valores
        setFormValues(dynamicValues);
      } else {
        // Modo creación: usar valores por defecto
        setFieldId(generateId());
        setFieldName("");
        setKeyValue("");

        const defaultValues: Record<string, unknown> = {};
        fieldConfig.sections.forEach((section) => {
          section.fields.forEach((field) => {
            if (field.key !== "fieldName") {
              defaultValues[field.key] = field.defaultValue;
            }
          });
        });
        setFormValues(defaultValues);
        setLocalListItems([]);
      }

      setLocked(true);
      setNewListItem("");
      setNewFileType("");
      setErrors({});
    }
  }, [isOpen, initialField, fieldConfig, type, generateId]);

  // Función para actualizar valores del formulario
  const updateFormValue = useCallback((key: string, value: unknown) => {
    setFormValues((prev) => {
      const updated = { ...prev, [key]: value };

      // Reset defaultValue when options_root_source changes
      if (key === "options_root_source" && prev.options_root_source !== value) {
        updated.defaultValue = "";
      }

      return updated;
    });

    // Limpiar error si existe
    setErrors((prevErrors) => {
      if (prevErrors[key]) {
        const newErrors = { ...prevErrors };
        delete newErrors[key];
        return newErrors;
      }
      return prevErrors;
    });
  }, []);

  // Función para validar el formulario
  const validate = (): boolean => {
    if (!fieldConfig) return false;

    const newErrors: Record<string, string> = {};

    // Validar fieldName (siempre requerido)
    if (!fieldName.trim()) {
      newErrors.fieldName = "El nombre del campo es requerido";
    } else if (fieldName.trim().toUpperCase() === "ID") {
      newErrors.fieldName = "No se puede crear un campo con el nombre 'ID'";
    }

    // Validar campos según la configuración
    fieldConfig.sections.forEach((section) => {
      section.fields.forEach((field) => {
        // fieldName se maneja por separado, ya lo validamos arriba
        if (field.key === "fieldName") {
          return;
        }

        // Verificar si el campo debe mostrarse según sus condiciones
        const isVisible = !field.condition || field.condition(formValues);

        if (isVisible && field.required) {
          const value = formValues[field.key];

          // Para switches, siempre son válidos (tienen valor booleano por defecto)
          if (field.type === "switch") {
            // Los switches siempre tienen un valor (true o false), así que son válidos
            // No necesitamos validar más
          }
          // Para listas locales, validar según el tipo y source
          else if (field.type === "local_list") {
            // Dropdown button lista local
            if (field.key === "localList") {
              const source = formValues.valueSource;
              if (source === "manual") {
                // Si es manual, validar localListItems
                if (localListItems.length === 0) {
                  newErrors[field.key] = `${field.label} es requerido`;
                }
              }
            }
            // Campo select/local_list lista local
            else if (field.key === "options_root_list") {
              const source = formValues.options_root_source;
              if (source === "manual") {
                // Si es manual, validar options_root_list
                const items = Array.isArray(value) ? value : [];
                if (items.length === 0) {
                  newErrors[field.key] = `${field.label} es requerido`;
                }
              }
            }
            // Otros casos de local_list
            else {
              const items = Array.isArray(value) ? value : [];
              if (items.length === 0) {
                newErrors[field.key] = `${field.label} es requerido`;
              }
            }
          }
          // Para file_types, verificar que tengan items
          else if (field.type === "file_types") {
            const items = Array.isArray(value) ? value : [];
            if (items.length === 0) {
              newErrors[field.key] = `${field.label} es requerido`;
            }
          }
          // Para centro de datos, validar según el source correspondiente
          else if (field.type === "datacenter") {
            const tableKey = `${field.key}_table`;
            const columnKey = `${field.key}_column`;

            // Dropdown button datacenter
            if (field.key === "valueDatacenter") {
              const source = formValues.valueSource;
              if (source === "database") {
                // Solo validar si el source es database
                if (!formValues[tableKey] || !formValues[columnKey]) {
                  newErrors[field.key] = `${field.label} es requerido`;
                }
              }
            }
            // Campo select/local_list datacenter
            else if (field.key === "options_root_datacenter") {
              const source = formValues.options_root_source;
              if (source === "database") {
                // Solo validar si el source es database
                if (!formValues[tableKey] || !formValues[columnKey]) {
                  newErrors[field.key] = `${field.label} es requerido`;
                }
              }
            }
            // Otros campos datacenter genéricos
            else {
              if (!formValues[tableKey] || !formValues[columnKey]) {
                newErrors[field.key] = `${field.label} es requerido`;
              }
            }
          }

          // Para otros campos, verificar que tengan valor
          // Si el campo tiene defaultValue, considerar que está bien incluso si es string vacío
          // Solo marcar como inválido si es undefined o null (no inicializado)
          else {
            if (value === undefined || value === null) {
              newErrors[field.key] = `${field.label} es requerido`;
            } else if (
              typeof value === "string" &&
              value === "" &&
              field.defaultValue === undefined
            ) {
              // Si es string vacío y no tiene defaultValue, es inválido
              newErrors[field.key] = `${field.label} es requerido`;
            }
          }

          // Validaciones personalizadas (solo si el campo tiene valor)
          if (
            field.validation &&
            value !== undefined &&
            value !== null &&
            value !== ""
          ) {
            if (
              field.validation.min !== undefined &&
              typeof value === "number" &&
              value < field.validation.min
            ) {
              newErrors[field.key] =
                `El valor mínimo es ${field.validation.min}`;
            }
            if (
              field.validation.max !== undefined &&
              typeof value === "number" &&
              value > field.validation.max
            ) {
              newErrors[field.key] =
                `El valor máximo es ${field.validation.max}`;
            }
            if (
              field.validation.minLength !== undefined &&
              typeof value === "string" &&
              value.length < field.validation.minLength
            ) {
              newErrors[field.key] =
                `Mínimo ${field.validation.minLength} caracteres`;
            }
            if (
              field.validation.maxLength !== undefined &&
              typeof value === "string" &&
              value.length > field.validation.maxLength
            ) {
              newErrors[field.key] =
                `Máximo ${field.validation.maxLength} caracteres`;
            }
            if (field.validation.pattern && typeof value === "string") {
              const regex = new RegExp(field.validation.pattern);
              if (!regex.test(value)) {
                newErrors[field.key] = "El formato no es válido";
              }
            }
            if (field.validation.custom) {
              const customError = field.validation.custom(value);
              if (customError) {
                newErrors[field.key] = customError;
              }
            }
          }
        }
      });
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Función para obtener los datos del campo listos para guardar
  const getFieldData = (): EntityField | null => {
    if (!type || !fieldName.trim()) return null;

    // Recoger todos los valores de datacenter, separando options_* de options_root_*
    const datacenterValues: Record<string, unknown> = {};
    let optionsTable: string | undefined;
    let optionsColumn: string | undefined;
    let optionsRootTable: string | undefined;
    let optionsRootColumn: string | undefined;

    if (fieldConfig) {
      fieldConfig.sections.forEach((section) => {
        section.fields.forEach((field) => {
          if (field.type === "datacenter") {
            const tableKey = `${field.key}_table`;
            const columnKey = `${field.key}_column`;

            // Si es valueDatacenter (dropdown button), guardar como options_table/options_column
            if (field.key === "valueDatacenter") {
              if (formValues[tableKey]) {
                optionsTable = formValues[tableKey] as string;
              }
              if (formValues[columnKey]) {
                optionsColumn = formValues[columnKey] as string;
              }
            }
            // Si es options_root_datacenter (select/local_list), guardar como options_root_table/options_root_column
            else if (field.key === "options_root_datacenter") {
              if (formValues[tableKey]) {
                optionsRootTable = formValues[tableKey] as string;
              }
              if (formValues[columnKey]) {
                optionsRootColumn = formValues[columnKey] as string;
              }
            }
            // Para otros campos datacenter, guardar con su nombre original
            else {
              if (formValues[tableKey]) {
                datacenterValues[tableKey] = formValues[tableKey];
              }
              if (formValues[columnKey]) {
                datacenterValues[columnKey] = formValues[columnKey];
              }
            }
          }
        });
      });
    }

    // Separar propiedades especiales que se manejan explícitamente
    const {
      required: requiredValue,
      configuration: formConfiguration,
      allowUserChooseDocType,
      documentType,
      options_root_list,
      options_root_source,
      valueSource,
      hasDropdownButton,
      buttonPosition,
      ...restFormValues
    } = formValues;

    // Remover los valores de datacenter de restFormValues
    Object.keys(datacenterValues).forEach((key) => {
      delete restFormValues[key];
    });

    // Para campos de identificación: si allowUserChooseDocType es true, usar "auto"
    const finalDocumentType =
      type === "identification" && allowUserChooseDocType
        ? "auto"
        : documentType;

    const fieldData: EntityField = {
      ...restFormValues,
      id: fieldId || generateId(),
      type: type === "local_list" ? "select" : type,
      name: fieldName,
      key: keyValue || parseCharacters(fieldName.toLowerCase()),
      required: typeof requiredValue === "boolean" ? requiredValue : false,
      configuration: {
        ...(formConfiguration || {}),
        type: type === "local_list" ? "select" : type,
        ...(type === "identification" && finalDocumentType
          ? { documentType: finalDocumentType }
          : {}),
        ...(hasDropdownButton !== undefined
          ? { buttonEnabled: hasDropdownButton }
          : {}),
        // Dropdown button: solo enviar si hasDropdownButton es true
        ...(hasDropdownButton
          ? {
              ...(buttonPosition ? { buttonPosition } : {}),
              ...(valueSource ? { options_source: valueSource } : {}),
              ...(valueSource === "database" && {
                ...(optionsTable && { options_table: optionsTable }),
                ...(optionsColumn && { options_column: optionsColumn }),
              }),
              ...(valueSource === "manual" &&
                localListItems.length > 0 && {
                  options_dropdown: localListItems,
                }),
            }
          : {}),
        // Campo select/local_list: según options_root_source, guardar tabla/columna O options
        ...(options_root_source ? { options_root_source } : {}),
        ...(options_root_source === "database" && {
          ...(optionsRootTable && { options_root_table: optionsRootTable }),
          ...(optionsRootColumn && { options_root_column: optionsRootColumn }),
        }),
        ...(options_root_source === "manual" &&
          Array.isArray(options_root_list) &&
          options_root_list.length > 0 && { options: options_root_list }),
        // Agregar todos los demás valores de datacenter a configuration
        ...datacenterValues,
      },
    };

    return fieldData;
  };

  // Función para actualizar valores de centro de datos
  const setDataCenterValue = useCallback(
    (fieldKey: string, tableName: string, columnName: string) => {
      setFormValues((prev) => ({
        ...prev,
        [`${fieldKey}_table`]: tableName,
        [`${fieldKey}_column`]: columnName,
      }));
    },
    [],
  );

  // Función para resetear el formulario
  const reset = () => {
    setFieldId("");
    setFieldNameState("");
    setKeyValue("");
    setFormValues({});
    setLocalListItems([]);
    setNewListItem("");
    setNewFileType("");
    setLocked(true);
    setErrors({});
  };

  // Calcular si el formulario es válido
  const isValid = useMemo(() => {
    if (!fieldConfig || !fieldName.trim()) {
      return false;
    }

    // Verificar que no haya errores
    if (Object.keys(errors).length > 0) {
      return false;
    }

    // Verificar campos requeridos visibles
    let hasAllRequired = true;
    const missingFields: string[] = [];

    fieldConfig.sections.forEach((section) => {
      section.fields.forEach((field) => {
        // fieldName se maneja por separado, ya lo validamos arriba
        if (field.key === "fieldName") {
          return;
        }

        const isVisible = !field.condition || field.condition(formValues);
        if (isVisible && field.required) {
          const value = formValues[field.key];

          // Para switches, siempre son válidos (tienen valor booleano por defecto)
          if (field.type === "switch") {
            // Los switches siempre tienen un valor (true o false), así que son válidos
            return;
          }

          // Para listas locales, validar según el tipo y source
          if (field.type === "local_list") {
            // Dropdown button lista local
            if (field.key === "localList") {
              const source = formValues.valueSource;
              if (source === "manual") {
                // Si es manual, validar localListItems
                if (localListItems.length === 0) {
                  hasAllRequired = false;
                  missingFields.push(field.key);
                }
              }
            }
            // Campo select/local_list lista local
            else if (field.key === "options_root_list") {
              const source = formValues.options_root_source;
              if (source === "manual") {
                // Si es manual, validar options_root_list
                const items = Array.isArray(value) ? value : [];
                if (items.length === 0) {
                  hasAllRequired = false;
                  missingFields.push(field.key);
                }
              }
            }
            // Otros casos de local_list
            else {
              const items = Array.isArray(value) ? value : [];
              if (items.length === 0) {
                hasAllRequired = false;
                missingFields.push(field.key);
              }
            }
            return;
          }

          // Para file_types, verificar que tengan items
          if (field.type === "file_types") {
            const items = Array.isArray(value) ? value : [];
            if (items.length === 0) {
              hasAllRequired = false;
              missingFields.push(field.key);
            }
            return;
          }

          // Para centro de datos, validar según el source correspondiente
          if (field.type === "datacenter") {
            const tableKey = `${field.key}_table`;
            const columnKey = `${field.key}_column`;

            // Dropdown button datacenter
            if (field.key === "valueDatacenter") {
              const source = formValues.valueSource;
              if (source === "database") {
                // Solo validar si el source es database
                if (!formValues[tableKey] || !formValues[columnKey]) {
                  hasAllRequired = false;
                  missingFields.push(field.key);
                }
              }
            }
            // Campo select/local_list datacenter
            else if (field.key === "options_root_datacenter") {
              const source = formValues.options_root_source;
              if (source === "database") {
                // Solo validar si el source es database
                if (!formValues[tableKey] || !formValues[columnKey]) {
                  hasAllRequired = false;
                  missingFields.push(field.key);
                }
              }
            }
            // Otros campos datacenter genéricos
            else {
              if (!formValues[tableKey] || !formValues[columnKey]) {
                hasAllRequired = false;
                missingFields.push(field.key);
              }
            }
            return;
          }

          // Para otros campos, verificar que tengan valor
          // Si el campo tiene defaultValue, considerar que está bien incluso si es string vacío
          // Solo marcar como inválido si es undefined o null (no inicializado)
          if (value === undefined || value === null) {
            hasAllRequired = false;
            missingFields.push(field.key);
          } else if (
            typeof value === "string" &&
            value === "" &&
            field.defaultValue === undefined
          ) {
            // Si es string vacío y no tiene defaultValue, es inválido
            hasAllRequired = false;
            missingFields.push(field.key);
          }
        }
      });
    });
    return hasAllRequired;
  }, [fieldConfig, fieldName, formValues, errors, localListItems]);

  return {
    // Valores
    fieldName,
    keyValue,
    formValues,
    localListItems,
    newListItem,
    newFileType,
    locked,

    // Estado de validación
    errors,
    isValid,
    isNew: initialField ? fieldId?.includes("field_") : true,

    // Funciones de actualización
    setFieldName,
    setKeyValue,
    updateFormValue,
    setLocalListItems,
    setNewListItem,
    setNewFileType,
    setLocked,
    setDataCenterValue,
    // Funciones de utilidad
    reset,
    getFieldData,
    validate,

    // Configuración
    fieldConfig: fieldConfig || null,
  };
};
